import type { dataCollection } from '$lib/api/rpc';

const anonymous = 'anonymous';
const guest = 'guest';
const member = 'member';
const collaborator = 'collaborator';
const moderator = 'moderator';
const owner = 'owner';
const orgadmin = 'orgadmin';
const root = 'root';

interface RolesI {
	anonymous: 'anonymous';
	guest: 'guest';
	member: 'member';
	collaborator: 'collaborator';
	moderator: 'moderator';
	owner: 'owner';
	orgadmin: 'orgadmin';
	root: 'root';
}

export type Role = keyof RolesI;

export const Roles: RolesI = {
	[anonymous]: anonymous,
	[guest]: guest,
	[member]: member,
	[collaborator]: collaborator,
	[moderator]: moderator,
	[owner]: owner,
	[orgadmin]: orgadmin,
	[root]: root,
};

export const RoleValues: { [index: string]: number } = {
	[anonymous]: 0,
	[guest]: 1,
	[member]: 2,
	[collaborator]: 3,
	[moderator]: 4,
	[owner]: 5,
	[orgadmin]: 6,
	[root]: 7,
};

type RoleAwareObject = {
	user_context?: { role: Role; org_role?: Role; hub_role?: Role };
	role: Role | string;
};

export const getRole = (obj: RoleAwareObject) => {
	const getRoleScore = (role: string): number => {
		if (!role) {
			return 0;
		}
		return RoleValues[role] || 0;
	};

	if (!obj.user_context || (!obj.user_context.org_role && !obj.user_context.hub_role)) {
		return obj.role;
	} else {
		const orgRole = obj.user_context.org_role || 'anonymous';
		const hubRole = obj.user_context.hub_role || 'anonymous';
		// greater value of user_context.org_role and user_context.hub_role
		const orgRoleValue = getRoleScore(orgRole);
		const hubRoleValue = getRoleScore(hubRole);
		return orgRoleValue >= hubRoleValue ? orgRole || hubRole : hubRole;
	}
};

const assertRole = (compare: (a: number, b: number) => boolean) => {
	return (role: Role | string) => {
		return (roleAwareObject: RoleAwareObject) => {
			if (typeof RoleValues[role] === 'undefined') {
				throw new Error(`Unexpected role passed: ${role}`);
			}
			if (!roleAwareObject) {
				return false;
			} else {
				const a = RoleValues[role];
				const leftRole = getRole(roleAwareObject);
				const b = RoleValues[leftRole];
				if (b === null || b === undefined) {
					return false;
				} else {
					return compare(a, b);
				}
			}
		};
	};
};

export const isRole = assertRole((a: number, b: number) => a === b);
export const atLeastRole = assertRole((a: number, b: number) => a <= b);

export const isAdmin = isRole(Roles.orgadmin);
export const atLeastCollaborator = atLeastRole(Roles.collaborator);
export const atLeastModerator = atLeastRole(Roles.moderator);
export const atLeastMember = atLeastRole(Roles.member);
export const atLeastOrgAdmin = atLeastRole(Roles.orgadmin);
export const atLeastOwner = atLeastRole(Roles.owner);

export const canPostInAnyCollection = (role: RoleAwareObject, collections: dataCollection[]) =>
	collections.some((collection) => RoleValues[collection.min_role] <= RoleValues[getRole(role)]);
